import React, { useState, useEffect, memo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const areEqual = (prevProps, nextProps) => {
  if (prevProps.services !== nextProps.services) return false;
  if (prevProps.subservices !== nextProps.subservices) return false;
  if (prevProps.language !== nextProps.language) return false;
  const prevSid = prevProps.currentService?.id ?? null;
  const nextSid = nextProps.currentService?.id ?? null;
  const prevSubid = prevProps.currentSubservice?.id ?? null;
  const nextSubid = nextProps.currentSubservice?.id ?? null;
  return prevSid === nextSid && prevSubid === nextSubid;
};

const ACCENT = '#27AAE1';
const BORDER = '1px solid rgba(0,0,0,0.08)';

const ServiceDetailSidebar = memo(({
  services,
  currentService,
  currentSubservice,
  subservices,
  language,
  onServiceClick,
  onSubserviceClick,
}) => {
  const navigate = useNavigate();
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    if (currentService?.id) setOpenId(prev => prev ?? currentService.id);
  }, [currentService?.id]);

  const getSubs = (serviceId) =>
    (subservices || []).filter(s => {
      const pid = s.parent_service_id ?? s.main_service_id ?? s.service_id ?? s.Subservices;
      return String(pid) === String(serviceId);
    });

  const goSub = useCallback((subId) => {
    if (onSubserviceClick) onSubserviceClick(subId);
    else navigate(`/${language}/subservice/${subId}`, { replace: false });
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [language, navigate, onSubserviceClick]);

  const goService = useCallback((service) => {
    if (onServiceClick) onServiceClick(service.id);
    else navigate(`/${language}/service/${service.id}`, { replace: false });
    setOpenId(service.id);
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [language, navigate, onServiceClick]);

  if (!services?.length) {
    return (
      <aside style={asideStyle}>
        <p style={emptyStyle}>{language === 'ar' ? 'لا توجد خدمات' : 'No services'}</p>
      </aside>
    );
  }

  return (
    <aside style={asideStyle}>
      <h3 style={titleStyle}>
        {language === 'ar' ? 'الخدمات' : 'Services'}
      </h3>
      <nav style={navStyle}>
        {services.map((service) => {
          const subs = getSubs(service.id);
          const isOpen = openId === service.id;
          const isCurrentService = currentService && String(service.id) === String(currentService.id);

          return (
            <div key={service.id} style={serviceBlockStyle}>
              <button
                type="button"
                className="sd-sidebar-btn"
                onClick={() => goService(service)}
                style={{
                  ...linkStyle,
                  ...(isCurrentService && !currentSubservice ? activeLinkStyle : {}),
                }}
              >
                {service.title || service.name}
              </button>
              {subs.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={() => setOpenId(isOpen ? null : service.id)}
                    style={toggleStyle}
                    aria-expanded={isOpen}
                  >
                    {subs.length} {language === 'ar' ? 'فرعية' : 'sub'}
                    <span style={{ ...chevronStyle, transform: isOpen ? 'rotate(90deg)' : 'none' }}>›</span>
                  </button>
                  {isOpen && (
                    <div style={subListStyle}>
                      {subs.map((sub) => {
                        const isCurrent = currentSubservice && String(sub.id) === String(currentSubservice.id);
                        return (
                          <button
                            key={sub.id}
                            type="button"
                            className="sd-sidebar-sub"
                            onClick={() => goSub(sub.id)}
                            style={{
                              ...subLinkStyle,
                              ...(isCurrent ? activeSubStyle : {}),
                            }}
                          >
                            {sub.title || sub.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </nav>
      <style>{`
        .sd-sidebar-btn:hover { background: rgba(39,170,225,0.08); }
        .sd-sidebar-sub:hover { background: rgba(39,170,225,0.06); }
      `}</style>
    </aside>
  );
}, areEqual);

const asideStyle = {
  position: 'relative',
  width: '100%',
  background: '#fff',
  borderRadius: '10px',
  border: BORDER,
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  overflow: 'hidden',
};

const titleStyle = {
  margin: 0,
  padding: '16px 18px',
  fontSize: '15px',
  fontWeight: '600',
  color: '#1a1a1a',
  borderBottom: BORDER,
};

const navStyle = {
  padding: '10px 12px 16px',
};

const serviceBlockStyle = {
  marginBottom: '4px',
};

const linkStyle = {
  display: 'block',
  width: '100%',
  padding: '10px 14px',
  textAlign: 'inherit',
  border: 'none',
  borderRadius: '8px',
  background: 'transparent',
  color: '#1a1a1a',
  fontSize: '14px',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'background 0.2s',
  fontFamily: 'inherit',
};

const activeLinkStyle = {
  background: 'rgba(39,170,225,0.1)',
  color: ACCENT,
  fontWeight: '600',
  borderInlineStart: `3px solid ${ACCENT}`,
  paddingInlineStart: '11px',
};

const toggleStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  padding: '6px 14px 6px 24px',
  border: 'none',
  background: 'transparent',
  color: '#64748b',
  fontSize: '12px',
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const chevronStyle = {
  display: 'inline-block',
  transition: 'transform 0.2s',
};

const subListStyle = {
  paddingInlineStart: '12px',
  marginTop: '2px',
  marginBottom: '6px',
};

const subLinkStyle = {
  display: 'block',
  width: '100%',
  padding: '8px 12px',
  textAlign: 'inherit',
  border: 'none',
  borderRadius: '6px',
  background: 'transparent',
  color: '#475569',
  fontSize: '13px',
  fontWeight: '400',
  cursor: 'pointer',
  transition: 'background 0.2s',
  fontFamily: 'inherit',
};

const activeSubStyle = {
  background: 'rgba(39,170,225,0.1)',
  color: ACCENT,
  fontWeight: '600',
};

const emptyStyle = {
  margin: 0,
  padding: '24px 18px',
  textAlign: 'center',
  color: '#94a3b8',
  fontSize: '14px',
};

ServiceDetailSidebar.displayName = 'ServiceDetailSidebar';

export default ServiceDetailSidebar;
